// S1 attribute chips (Module 01 §4: "attribute chips (mm², colour swatch
// rendered from color_code)"). Renders a product's `attributes` as compact chips
// using the attribute schema's `ui_hints` for labels + units — the SAME
// data-driven source the schema form uses, so nothing here is vertical-specific
// (doctrine rule 5: industry differences are data, never code paths).
//
// Colour swatch: a field is treated as a colour when the schema's ui_hints flag
// it (`ui_hints.swatches: ["color_code", …]`) or its key reads like a colour.
// The value is resolved to a CSS colour via (a) a direct CSS hex/name, or (b) the
// IEC 60757 cable colour-letter codes (an international standard, not one pack's
// invention). Anything unresolved simply renders as text — never a broken swatch.
//
// The colour TABLES live in `./iec-60757.ts`: they are reference data about the
// physical world, not design tokens, and keeping them out of a component file
// keeps "raw colour literals live in tokens.css" true of the presentation tree.

import { useTranslation } from "react-i18next";
import { CSS_COLOUR_NAMES, IEC_60757 } from "./iec-60757";
import { parseUiHints, type AttrHints } from "../../components/ui-hints";

export type { AttrHints };

// ---- colour resolution ------------------------------------------------------

const HEX_RE = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/u;

/**
 * Resolve an attribute value to a CSS colour string, or `undefined` when it is
 * not a recognizable colour (caller then renders text only). Pure + exported.
 */
export function resolveSwatchColor(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const raw = value.trim();
  if (raw.length === 0) return undefined;
  if (HEX_RE.test(raw)) return raw;
  const lower = raw.toLowerCase();
  if (CSS_COLOUR_NAMES.has(lower)) return lower;
  // IEC codes may be compound (e.g. "GN/YE"); use the first recognized segment.
  const head = raw.toUpperCase().split(/[/-]/)[0];
  if (head !== undefined && head in IEC_60757) return IEC_60757[head];
  return undefined;
}

/** A field is a colour field when ui_hints marks it or its key reads as colour. */
export function isColourField(key: string, hints: AttrHints): boolean {
  return hints.swatches.has(key) || /colou?r/i.test(key);
}

// ---- chips ------------------------------------------------------------------

function formatValue(value: unknown, trueLabel: string, falseLabel: string): string {
  if (typeof value === "boolean") return value ? trueLabel : falseLabel;
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  if (value === null || value === undefined) return "";
  return JSON.stringify(value);
}

/** Order attribute keys: ui_hints.order first, then the rest alphabetically. */
export function orderAttributeKeys(
  attributes: Record<string, unknown>,
  hints: AttrHints,
): string[] {
  const present = Object.keys(attributes).filter((k) => {
    const v = attributes[k];
    return v !== undefined && v !== null && v !== "";
  });
  const ordered = hints.order.filter((k) => present.includes(k));
  const rest = present.filter((k) => !hints.order.includes(k)).sort((a, b) => a.localeCompare(b));
  return [...ordered, ...rest];
}

export function ProductAttributeChips({
  attributes,
  uiHints,
  max = 4,
}: {
  attributes: Record<string, unknown>;
  uiHints?: Record<string, unknown> | undefined;
  max?: number | undefined;
}) {
  const { t, i18n } = useTranslation();
  const hints = parseUiHints(uiHints, i18n.language);
  const keys = orderAttributeKeys(attributes, hints);
  if (keys.length === 0) return null;

  const trueLabel = t("catalogue.attr.booleanTrue");
  const falseLabel = t("catalogue.attr.booleanFalse");
  const shown = keys.slice(0, max);
  const overflow = keys.length - shown.length;

  return (
    <span className="attr-chips">
      {shown.map((key) => {
        const raw = attributes[key];
        const label = hints.labels[key] ?? key;
        const unit = hints.units[key];
        const swatch = isColourField(key, hints) ? resolveSwatchColor(raw) : undefined;
        const text = formatValue(raw, trueLabel, falseLabel);
        return (
          <span className="attr-chip" key={key} data-attr={key} title={`${label}: ${text}`}>
            {swatch !== undefined ? (
              <span
                className="attr-swatch"
                style={{ backgroundColor: swatch }}
                aria-hidden="true"
                data-swatch={swatch}
              />
            ) : null}
            {/* The LABEL renders, it is not a hover-only `title`. A column of
             * bare numbers ("70 3 1.5") says nothing, and a phone has no way
             * to reveal a tooltip at all — which is how "copper 70 3 pvc"
             * shipped as the ATTRIBUTES column. */}
            <span className="attr-chip-label">{label}</span>
            <span className="attr-chip-value">{text}</span>
            {unit !== undefined ? <span className="attr-chip-unit">{unit}</span> : null}
          </span>
        );
      })}
      {overflow > 0 ? (
        <span className="attr-chip attr-chip-more">
          {t("catalogue.productsList.chipMore", { count: overflow })}
        </span>
      ) : null}
    </span>
  );
}
